// ContributorMemoryDetailPage.jsx — /contributing/:spaceId/memories/:memId
//
// v0.1 — NEW-65 (PR-7b): read-only view of a SHARED memory a contributor did not
//   author (another contributor's approved/group memory, or an owner-shared memory).
//   VIEW-ONLY by design — no edit, share, favorite, or remove controls. The
//   contributor's OWN memories are edited via the pencil on the feed, not here.
//
// Architecture (grounded in the best-practice review 2026-09-05):
//   - Reuse-first: composes the already-shared media components (VoiceCard,
//     VideoCard) rather than extracting a new shared view (Rule of Three not
//     met; only one live read-only surface today — see session log). The
//     header/text/photo layout mirrors MemoryDetailPage's contributor branch.
//   - Auth boundary (OWASP least privilege): contributor-scoped access only,
//     via getContributorAccess('linked') → owner-JWT client + '/me/contributions'.
//     This page is NEVER routed through the owner-scoped MemoryDetailPage, which
//     reads /spaces/{spaceId} and would 403 for a contributor.
//   - Data source (Option A): the tapped memory arrives via router state
//     (location.state.viewMemory), mirroring the deployed edit pages
//     (state.editMemory). location.state is dropped on hard refresh / new tab
//     (React Router discussion #8919) — a KNOWN trade-off accepted for parity
//     and zero backend change. Guarded: no state → redirect to the feed, never
//     a broken screen. A fetch-by-URL endpoint is the principled future upgrade.
//   - Media signed URLs use GET /media/playback/:key (contributor-authorized):
//     VoiceCard self-fetches from voiceNote.s3Key; photo and video URLs are
//     fetched here (VideoCard expects pre-populated playbackUrl/posterUrl).
//
// Auth: Auth0 JWT (linked account) via getContributorAccess('linked'); the route
//   is wrapped in <ProtectedRoute> in App.jsx.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getContributorAccess } from '../api/contributorAccess';
import VoiceCard from '../components/VoiceCard';
import VideoCard from '../components/VideoCard';

function BackArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

export default function ContributorMemoryDetailPage({ mode = 'linked' }) {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const viewMemory = location.state?.viewMemory || null;
  const category = (viewMemory?.category || '').toLowerCase();

  // Single source of truth for client + prefix, bound to the access mode.
  const access = useMemo(
    () => getContributorAccess(mode, getAccessTokenSilently),
    [mode, getAccessTokenSilently]
  );
  // Stable getApi for VoiceCard (mirrors the feed's factory pattern).
  const getApi = useCallback(() => access.api, [access]);

  const [photoUrl, setPhotoUrl] = useState(null);
  const [videoMemory, setVideoMemory] = useState(null);

  const feedPath = `/contributing/${spaceId}/memories`;

  /* Guard: no router state (hard refresh / pasted URL) → back to the feed.
     location.state is not durable across a reload — Option A trade-off. */
  useEffect(() => {
    if (!viewMemory) navigate(feedPath, { replace: true });
  }, [viewMemory, navigate, feedPath]);

  /* Photo: fetch the signed URL (same route the feed uses). */
  useEffect(() => {
    if (category !== 'photo' || !viewMemory?.s3Key) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await access.api.get(
          `/media/playback/${encodeURIComponent(viewMemory.s3Key)}`
        );
        if (!cancelled) setPhotoUrl(data.playbackUrl);
      } catch (err) {
        console.error('Photo URL fetch error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [category, viewMemory, access]);

  /* Video: fetch playback + poster signed URLs and reshape into the shape
     VideoCard expects (playbackUrl / posterUrl / durationSeconds). The feed
     returns only keys (playbackKey / posterKey / duration). */
  useEffect(() => {
    if (category !== 'video' || !viewMemory?.videoNote?.playbackKey) return;
    let cancelled = false;
    (async () => {
      try {
        const vn = viewMemory.videoNote;
        const [playRes, posterRes] = await Promise.all([
          access.api.get(`/media/playback/${encodeURIComponent(vn.playbackKey)}`),
          vn.posterKey
            ? access.api.get(`/media/playback/${encodeURIComponent(vn.posterKey)}`)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setVideoMemory({
          ...viewMemory,
          videoNote: {
            playbackUrl: playRes.playbackUrl,
            posterUrl: posterRes ? posterRes.playbackUrl : null,
            durationSeconds: vn.duration || 0,
          },
        });
      } catch (err) {
        console.error('Video URL fetch error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [category, viewMemory, access]);

  if (!viewMemory) return null; // redirecting to the feed

  const creatorName = viewMemory.creatorName;
  const subtitle = creatorName ? `Shared by ${creatorName}` : 'Shared memory';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-cream, #FBF9F4)',
        fontFamily: "var(--font-sans, 'DM Sans', -apple-system, sans-serif)",
        color: 'var(--color-text, #2f2b26)',
      }}
    >
      {/* Header: back + "Shared by {name}" (RO-1). */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          borderBottom: '1px solid var(--color-border-light, #edebe7)',
          position: 'sticky',
          top: 0,
          background: 'var(--color-cream, #FBF9F4)',
          zIndex: 5,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(feedPath)}
          aria-label="Back to shared memories"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-sage-dark, #5b7a65)',
            flexShrink: 0,
            padding: 0,
          }}
        >
          <BackArrowIcon />
        </button>
        <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#4a4a4a' }}>
          {subtitle}
        </p>
      </header>

      <main
        style={{
          maxWidth: '640px',
          margin: '0 auto',
          padding: '24px 16px 64px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
        }}
      >
        {viewMemory.title && (
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 600, lineHeight: 1.3 }}>
            {viewMemory.title}
          </h2>
        )}

        {/* Photo */}
        {category === 'photo' && (
          photoUrl ? (
            <img
              src={photoUrl}
              alt={viewMemory.title || 'Photo memory'}
              style={{ width: '100%', borderRadius: '14px', display: 'block' }}
            />
          ) : (
            <div
              aria-hidden="true"
              style={{
                width: '100%', height: '260px', borderRadius: '14px',
                background: 'var(--color-cream-2, #EFEADF)',
              }}
            />
          )
        )}

        {/* Voice — VoiceCard self-fetches playback via getApi. View-only: no callbacks. */}
        {category === 'voice' && (
          <VoiceCard memory={viewMemory} getApi={getApi} embedded />
        )}

        {/* Video — reshaped memory with signed URLs. View-only: no callbacks. */}
        {category === 'video' && videoMemory && (
          <VideoCard memory={videoMemory} embedded />
        )}
        {category === 'video' && !videoMemory && (
          <div
            aria-hidden="true"
            style={{
              width: '100%', height: '260px', borderRadius: '14px',
              background: 'var(--color-cream-2, #EFEADF)',
            }}
          />
        )}

        {/* Note / caption / text body */}
        {viewMemory.note && (
          <p style={{ margin: 0, fontSize: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {viewMemory.note}
          </p>
        )}
      </main>
    </div>
  );
}
